import axios, { isAxiosError } from 'axios';
import { Button } from '@vimily/bonjoro-ui-components-v2/Button';
import { Modal } from '@vimily/bonjoro-ui-components-v2/Modal';
import React, { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import {
  AppSumoCodeList,
  getCodeInputName,
} from '~/Public/Modules/Registration/AppSumoRegistration/AppSumoCodeList';
import { useZiggy } from '~/react-hooks/use-ziggy';

const MAX_APPSUMO_CODES = 5;

export interface AddAppSumoCodesModalProps {
  title?: string;
  open: boolean;
  onModalClose: () => void;
  onAddSuccess?: () => void;
  maxCodes?: number;
}

interface FieldData {
  appsumo_codes: Array<{
    value: string;
  }>;
}

export default function AddAppSumoCodesModal({
  title = 'Add more AppSumo codes',
  open,
  maxCodes = MAX_APPSUMO_CODES,
  onModalClose,
  onAddSuccess,
}: AddAppSumoCodesModalProps) {
  const [loading, setLoading] = useState(false);
  const methods = useForm<FieldData>();
  const { handleSubmit, setError } = methods;
  const { route } = useZiggy();

  async function onSubmit(data: FieldData) {
    try {
      setLoading(true);

      await axios.post(route('billing::appsumo.addCodes'), {
        appsumo_codes: data.appsumo_codes.map((c) => c.value),
      });

      onAddSuccess?.();
      setLoading(false);
    } catch (err) {
      if (
        isAxiosError<
          ApiErrorResponse<{
            appsumo_codes: Record<string, string>[];
          }>
        >(err)
      ) {
        const codeErrors = err.response?.data.errors.appsumo_codes ?? [];

        codeErrors.forEach((value, index) => {
          const name = getCodeInputName(index);

          setError(name, {
            type: 'manual',
            message: value[Object.keys(value)[0]],
          });
        });
      }

      setLoading(false);
    }
  }

  return (
    <Modal open={open} onModalClose={onModalClose} className='max-w-[510px]'>
      <h1 className='v2-text-neutral-900 text-lg pt-0'>{title}</h1>

      <p className='v2-text-neutral-900'>
        You can add up to 5 AppSumo codes. You can add {maxCodes} more.
      </p>

      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <AppSumoCodeList maxCodes={maxCodes} />

          <Button
            variant={loading ? 'disabled' : 'primary'}
            large
            className='mt-5 w-full'
          >
            {loading && <span className='fa fa-spinner spinning mr-2' />}
            Add codes
          </Button>
        </form>
      </FormProvider>
    </Modal>
  );
}
